import React from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Typography, Space, Divider } from 'antd';
import useBrowseCategories from '../hooks/useBrowseCategories';
import {
    MailOutlined,
    PhoneOutlined,
    EnvironmentOutlined,
    TeamOutlined,
    ReadOutlined,
    ShopOutlined,
    InstagramOutlined,
    FacebookOutlined,
    TwitterOutlined,
    LinkedinOutlined,
    ArrowRightOutlined,
} from '@ant-design/icons';

const FOOTER_TAB_CONFIG = [
    { key: 'community',  label: 'Community',  icon: <TeamOutlined />,        param: 'community' },
    { key: 'education',  label: 'Education',  icon: <ReadOutlined />,        param: 'education' },
    { key: 'location',   label: 'Location',   icon: <EnvironmentOutlined />, param: 'city' },
    { key: 'profession', label: 'Profession', icon: <ShopOutlined />,        param: 'occupation' },
];



const { Title, Text } = Typography;

const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

const FooterLink = ({ to, children }) => (
    <Link
        to={to}
        onClick={scrollTop}
        style={{
            color: 'rgba(255,255,255,0.78)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '0.9rem',
            transition: 'all 0.2s ease',
            textDecoration: 'none',
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.color = 'white';
            e.currentTarget.style.textDecoration = 'underline';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.color = 'rgba(255,255,255,0.78)';
            e.currentTarget.style.textDecoration = 'none';
        }}
    >
        {children}
    </Link>
);

const SocialIcon = ({ href, icon }) => (
    <a
        href={href}
        target="_blank"
        rel="noreferrer"
        style={{
            width: '40px', height: '40px',
            borderRadius: '10px',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white',
            transition: 'all 0.25s ease',
            textDecoration: 'none',
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.22)';
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
        }}
    >
        {React.cloneElement(icon, { style: { fontSize: '1rem' } })}
    </a>
);

const LandingFooter = () => {
    const { categories } = useBrowseCategories();

    const footerBrowse = FOOTER_TAB_CONFIG.map(t => ({
        ...t,
        items: (categories[t.key] ?? []).map(item => ({
            label: item.label,
            href: `/profiles?${t.param}=${encodeURIComponent(item.label)}`,
        })),
    }));

    return (
        <footer
            style={{
                background: 'linear-gradient(135deg, #6A0572 0%, #880E6E 40%, #AD1457 100%)',
                padding: '72px 24px 28px',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                fontFamily: 'Calibri, sans-serif',
            }}
        >
            {/* Decorative dots */}
            <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
                pointerEvents: 'none',
            }} />

            {/* Decorative blobs */}
            <div style={{
                position: 'absolute', top: '-80px', right: '-60px',
                width: '300px', height: '300px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />
            <div style={{
                position: 'absolute', bottom: '-50px', left: '-40px',
                width: '220px', height: '220px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />

            <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>

                {/* ── Browse profiles row ── */}
                <div style={{ marginBottom: '16px' }}>
                    <div style={{
                        color: 'rgba(255,255,255,0.45)',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        letterSpacing: '1.4px',
                        textTransform: 'uppercase',
                        marginBottom: '20px',
                    }}>
                        Browse Profiles
                    </div>
                    <Row gutter={[32, 32]}>
                        {footerBrowse.map(group => (
                            <Col xs={12} md={6} key={group.key}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                    marginBottom: '12px',
                                }}>
                                    {React.cloneElement(group.icon, { style: { color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' } })}
                                    <span style={{ color: 'white', fontWeight: 700, fontSize: '0.88rem' }}>
                                        {group.label}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                                    {group.items.map(item => (
                                        <FooterLink key={item.label} to={item.href}>
                                            {item.label}
                                        </FooterLink>
                                    ))}
                                    <Link
                                        to={`/profiles?browse=${group.key}`}
                                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                        style={{
                                            color: '#F9A8C9',
                                            fontSize: '0.78rem',
                                            fontWeight: 600,
                                            textDecoration: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            marginTop: '2px',
                                            transition: 'color 0.18s ease',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = '#F9A8C9'; }}
                                    >
                                        View all <ArrowRightOutlined style={{ fontSize: '0.68rem' }} />
                                    </Link>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>

                <Divider style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '32px 0 48px' }} />

                <Row gutter={[40, 48]}>

                    {/* Brand column */}
                    <Col xs={24} md={8} lg={7}>
                        <img
                            src={`${process.env.PUBLIC_URL}/oottan_com_white_background.png`}
                            alt="Oottan"
                            style={{ height: '48px', width: 'auto', objectFit: 'contain', marginBottom: '20px' }}
                        />
                        <Text style={{
                            color: 'rgba(255,255,255,0.72)',
                            display: 'block',
                            marginBottom: '24px',
                            lineHeight: '1.7',
                            fontSize: '0.88rem',
                        }}>
                            Dedicated to helping you find your perfect life partner with trust and confidence.
                        </Text>

                        {/* Certification badges */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                            <a
                                href="https://www.hirehike.com/HIREHIKE_TECHNOLOGIES_PRIVATE_LIMITED_ISO_9001.jpg"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="ISO 9001:2015 certificate"
                                title="ISO 9001:2015 certificate"
                                style={{ display: 'block', width: '108px', height: '144px', overflow: 'hidden', borderRadius: '3px' }}
                            >
                                <img
                                    src="https://www.hirehike.com/Hirehike_ISO_9001_27001.png"
                                    alt="ISO 9001:2015 certified"
                                    style={{ height: '100%', width: 'auto', maxWidth: 'none' }}
                                />
                            </a>
                            <a
                                href="https://www.hirehike.com/HIREHIKE_TECHNOLOGIES_PRIVATE_LIMITED_ISO_27001.jpg"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="ISO 27001:2022 certificate"
                                title="ISO 27001:2022 certificate"
                                style={{ display: 'block', width: '108px', height: '144px', overflow: 'hidden', borderRadius: '3px' }}
                            >
                                <img
                                    src="https://www.hirehike.com/Hirehike_ISO_9001_27001.png"
                                    alt="ISO 27001:2022 certified"
                                    style={{ height: '100%', width: 'auto', maxWidth: 'none', transform: 'translateX(-50%)' }}
                                />
                            </a>
                        </div>
                        <img
                            src="https://www.hirehike.com/nvidia-inception-program-badge-rgb-for-screen.jpg"
                            alt="NVIDIA Inception Program member"
                            style={{ height: '56px', width: 'auto', objectFit: 'contain', marginBottom: '24px', display: 'block' }}
                        />

                        {/* Social icons */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '28px', flexWrap: 'wrap' }}>
                            <SocialIcon href="https://www.facebook.com/hirehike" icon={<FacebookOutlined />} />
                            <SocialIcon href="https://www.twitter.com/hirehike" icon={<TwitterOutlined />} />
                            <SocialIcon href="https://www.instagram.com/hirehike" icon={<InstagramOutlined />} />
                            <SocialIcon href="https://www.linkedin.com/company/hirehike" icon={<LinkedinOutlined />} />
                        </div>

                    </Col>

                    {/* Company Links */}
                    <Col xs={12} md={4} lg={4}>
                        <Title level={5} style={{
                            color: 'white', marginBottom: '20px',
                            fontWeight: '700', letterSpacing: '0.3px', fontSize: '0.95rem',
                        }}>
                            Company
                        </Title>
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <FooterLink to="/message-from-ceo">Message from CEO</FooterLink>
                            <FooterLink to="/contact-us">Contact Us</FooterLink>
                            <FooterLink to="/pricing">View Plans</FooterLink>
                        </Space>
                    </Col>

                    {/* Legal Links */}
                    <Col xs={12} md={5} lg={5}>
                        <Title level={5} style={{
                            color: 'white', marginBottom: '20px',
                            fontWeight: '700', letterSpacing: '0.3px', fontSize: '0.95rem',
                        }}>
                            Legal
                        </Title>
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
                            <FooterLink to="/cookie-policy">Cookie Policy</FooterLink>
                            <FooterLink to="/terms-conditions">Terms & Conditions</FooterLink>
                            <FooterLink to="/return-refund-policy">Return & Refund Policy</FooterLink>
                        </Space>
                    </Col>

                    {/* Contact */}
                    <Col xs={24} md={7} lg={8}>
                        <Title level={5} style={{
                            color: 'white', marginBottom: '20px',
                            fontWeight: '700', letterSpacing: '0.3px', fontSize: '1rem',
                        }}>
                            Contact Us
                        </Title>
                        <Space direction="vertical" size={18}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                <div style={{
                                    width: '34px', height: '34px', borderRadius: '10px', flexShrink: 0,
                                    background: 'rgba(255,255,255,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <EnvironmentOutlined style={{ fontSize: '1rem', color: 'white' }} />
                                </div>
                                <Text style={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.88rem', lineHeight: '1.6' }}>
                                    Hirehike Technologies Private Limited,<br />
                                    8/61/1, Kumbavilai, Chaicode,<br />
                                    Veeyanoor, Kanyakumari District,<br />
                                    Tamil Nadu, India – 629177
                                </Text>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div style={{
                                    width: '34px', height: '34px', borderRadius: '10px', flexShrink: 0,
                                    background: 'rgba(255,255,255,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <MailOutlined style={{ fontSize: '1rem', color: 'white' }} />
                                </div>
                                <Text style={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.88rem' }}>
                                    info[at]oottan[dot]com
                                </Text>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div style={{
                                    width: '34px', height: '34px', borderRadius: '10px', flexShrink: 0,
                                    background: 'rgba(255,255,255,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <PhoneOutlined style={{ fontSize: '1rem', color: 'white' }} />
                                </div>
                                <Text style={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.88rem' }}>
                                    +91 7904428253
                                </Text>
                            </div>
                        </Space>
                    </Col>
                </Row>

                <Divider style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '48px 0 24px' }} />

                <div style={{ textAlign: 'center' }}>
                    <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem' }}>
                        © {new Date().getFullYear()} Hirehike Technologies Private Limited.
                        {' '}Hirehike® is a registered trademark of Hirehike Technologies Private Limited.
                    </Text>
                </div>
            </div>
        </footer>
    );
};

export default LandingFooter;
